<template>
  <div
    class="relative"
    :class="{
      'container p0 px50-desktop': !isMenuSearchBar,
      'w-100 menu-search': isMenuSearchBar && !searchSuggestions,
      'absolute menu-search-active top-0': isMenuSearchBar && searchSuggestions
    }"
  >
    <div
      v-if="searchSuggestions && (search || isMobileViewport) && !isMenuSearchBar"
      class="search-block-spacer"
      aria-hidden="true"
    />
    <div
      class="flex search-block align-items-center"
      :class="{
        'c-bg-white': isMenuSearchBar,
        'search-block--glass': !isMenuSearchBar,
        'search-block--open': searchSuggestions && (search || isMobileViewport) && !isMenuSearchBar
      }"
    >
      <base-input
        id="search"
        ref="search"
        v-model="search"
        class="search-block-input c-black w-100"
        :class="{ 'show-search-icon': shouldShowIcon }"
        :placeholder="inputHasFocus ? '' : searchPlaceholder"
        :is-search="true"
        :is-menu-search="isMenuSearchBar"
        type="text"
        autocomplete="off"
        :maxlength="MAX_SEARCH_LENGTH"
        @input="onInput"
        @focus="onInputFocus"
        @blur="onInputBlur"
      />
      <button
        v-if="(!isMobileViewport && search) || (isMobileViewport && searchSuggestions && !isMenuSearchBar)"
        type="button"
        class="search-clear"
        :aria-label="isMobileViewport ? 'Close search' : 'Clear search'"
        @click="isMobileViewport ? closeMobileSearch() : clearSearch()"
      >
        <i class="icon icon-close-white" />
      </button>
      <button-secondary
        v-show="!shouldShowIcon"
        class="search-button align-items-center flex py0 ml2 px22 px28-desktop ml4-desktop c-bg-transparent"
        :class="{
          'hidden': isMenuSearchBar && !searchSuggestions,
          'search-button-suggestion ml3 lh29 bold': isMenuSearchBar && searchSuggestions
        }"
        @click.native="runSearch"
      >
        <img
          src="~theme/assets/icons/search.svg"
          alt="search"
        >
      </button-secondary>
    </div>

    <transition name="fade">
      <div
        v-show="searchSuggestions && search && !isMobileViewport"
        class="search-backdrop-overlay"
        @click="hideSuggestions()"
      />
    </transition>
    <transition name="search-overlay">
      <div
        v-show="searchSuggestions && (search || isMobileViewport)"
        class="absolute right-0 left-0 shadow suggestion-block px9 align-left mx50-desktop"
        :class="[isMenuSearchBar ? 'c-bg-white c-black suggestion-block-menu' : 'search-block--glass']"
      >
        <div
          v-if="didYouMean"
          class="suggestion-did-you-mean"
        >
          {{ $t('Did you mean {name}?', { name: didYouMean }) }}
        </div>
        <transition name="fade">
          <div
            v-if="hasNoResults && getNoResultsMessage"
            class="no-results suggestion-empty"
          >
            {{ $t(getNoResultsMessage) }}
          </div>
        </transition>
        <div class="suggestion-desktop hidden-mobile">
          <div class="suggestion-col suggestion-col--artists">
            <div
              v-for="category in categories"
              :key="category.id"
              class="suggestion-artist-item"
              role="link"
              tabindex="0"
              @click="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
              @keydown.enter.prevent="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
              @keydown.space.prevent="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
            >
              <img
                class="suggestion-artist-thumb"
                :src="getArtistImage(category)"
                :alt="category.name"
              >
              <div class="suggestion-artist-meta">
                <div class="suggestion-title fs20 lh24">
                  {{ category.name }}
                </div>
                <div class="suggestion-artist-count fs18 lh22">
                  {{ $t(getCategoryLabel(category)) }} • {{ getCategoryEventCount(category) }} {{ $t('events') }}
                </div>
              </div>
            </div>
          </div>

          <div class="suggestion-col suggestion-col--events">
            <div
              v-for="product in visibleProducts"
              :key="product.id"
              class="suggestion-event-item"
              role="link"
              tabindex="0"
              @click="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
              @keydown.enter.prevent="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
              @keydown.space.prevent="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
            >
              <div
                class="suggestion-date-badge"
                aria-hidden="true"
              >
                <div class="fs24 lh24 bold">
                  {{ getEventDay(product) }}
                </div>
                <div class="fs18 lh18">
                  {{ getEventMonth(product) }}
                </div>
              </div>
              <div class="suggestion-event-meta">
                <div class="suggestion-title fs20 lh24">
                  {{ product.name }}
                </div>
                <div class="suggestion-event-sub fs18 lh24">
                  <div
                    v-if="getEventWeekdayShort(product) || getEventTime(product)"
                    class="suggestion-event-line"
                  >
                    <i class="icon icon-schedule-white" />
                    <span class="suggestion-event-line-text">
                      {{ [getEventWeekdayShort(product), getEventTime(product)].filter(Boolean).join(', ') }}
                    </span>
                  </div>
                  <div
                    v-if="getLocationLabel(product) || getCityLabel(product)"
                    class="suggestion-event-line"
                  >
                    <i class="icon icon-marker-white" />
                    <span class="suggestion-event-line-text">
                      {{ [getLocationLabel(product), getCityLabel(product)].filter(Boolean).join(', ') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="suggestion-mobile hidden-tablet hidden-desktop"
          @touchstart.passive="onResultsTouchStart"
          @scroll.passive="onResultsScroll"
        >
          <template v-if="search">
            <div
              v-for="category in categories"
              :key="category.id"
              class="suggestion-mobile-artist"
              role="link"
              tabindex="0"
              @click="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
              @keydown.enter.prevent="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
              @keydown.space.prevent="onSuggestionNavigate(categoryLink(category), {'type': 'category', 'id': category.name})"
            >
              <img
                class="suggestion-mobile-artist-thumb"
                :src="getArtistImage(category)"
                :alt="category.name"
              >
              <div class="suggestion-mobile-artist-meta">
                <div class="suggestion-mobile-title">
                  {{ category.name }}
                </div>
                <div class="suggestion-mobile-sub">
                  {{ $t(getCategoryLabel(category)) }} • {{ getCategoryEventCount(category) }} {{ $t('events') }}
                </div>
              </div>
            </div>
            <div class="suggestion-mobile-events">
              <div
                v-for="product in visibleProducts"
                :key="product.id"
                class="suggestion-mobile-event"
                role="link"
                tabindex="0"
                @click="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
                @keydown.enter.prevent="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
                @keydown.space.prevent="onSuggestionNavigate(productLink(product), {'type': 'product', 'id':product.sku})"
              >
                <div
                  class="suggestion-mobile-date"
                  aria-hidden="true"
                >
                  <div class="suggestion-mobile-date-day">
                    {{ getEventDay(product) }}
                  </div>
                  <div class="suggestion-mobile-date-month">
                    {{ getEventMonth(product) }}
                  </div>
                </div>
                <div class="suggestion-mobile-event-meta">
                  <div class="suggestion-mobile-title">
                    {{ product.name }}
                  </div>
                  <div class="suggestion-mobile-event-lines">
                    <div
                      v-if="getEventWeekdayShort(product) || getEventTime(product)"
                      class="suggestion-mobile-line"
                    >
                      <i class="icon icon-schedule-white" />
                      <span class="suggestion-mobile-line-text">
                        {{ [getEventWeekdayShort(product), getEventTime(product)].filter(Boolean).join(', ') }}
                      </span>
                    </div>
                    <div
                      v-if="getLocationLabel(product) || getCityLabel(product)"
                      class="suggestion-mobile-line"
                    >
                      <i class="icon icon-marker-white" />
                      <span class="suggestion-mobile-line-text">
                        {{ [getLocationLabel(product), getCityLabel(product)].filter(Boolean).join(', ') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SearchPanel from '@vue-storefront/core/compatibility/components/blocks/SearchPanel/SearchPanel'
import VueOfflineMixin from 'vue-offline/mixin'
import { minLength } from 'vuelidate/lib/validators'
import ButtonSecondary from 'theme/components/theme/ButtonSecondary'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput'
import { mapState, mapGetters } from 'vuex'
import config from 'config'
import { formatCategoryLink, formatProductLink } from '@vue-storefront/core/modules/url/helpers'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { timeToDayJS } from 'theme/helpers/timeHelper'
import headerPlaceholder from 'theme/assets/header_placeholder.jpg'
import ScrollLock from 'theme/mixins/scrollLock'
import debounce from 'lodash-es/debounce'
import { normalizeTerm, matchesNormalized, deriveDidYouMean } from 'theme/helpers/searchMatch'
import { boundSearchText } from '@vue-storefront/core/lib/search/adapter/api/searchTextQuery'
import { Logger } from '@vue-storefront/core/lib/logger'

const SEARCH_DEBOUNCE_MS = 200
const MAX_SEARCH_LENGTH = 128
const ITEM_LIMIT = 6
const ITEM_LIMIT_MOBILE = 4

export default {
  components: {
    BaseInput,
    ButtonSecondary
  },
  mixins: [SearchPanel, VueOfflineMixin, ScrollLock],
  validations: {
    search: {
      minLength: minLength(1)
    }
  },
  props: {
    isMenuSearchBar: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      selectedCategoryIds: [],
      size: ITEM_LIMIT,
      inputHasFocus: false,
      inputHasValue: false,
      viewportWidth: 1024,
      pendingSearches: 0,
      inputDirty: false,
      searchRequestId: 0,
      MAX_SEARCH_LENGTH
    }
  },
  computed: {
    ...mapGetters({
      getCategories: 'category-next/getMenuCategories',
      getAttributesByCode: 'attribute/attributeListByCode'
    }),
    currentLocale () {
      return currentStoreView()?.i18n?.defaultLocale || 'sv-SE'
    },
    isMobileViewport () {
      return this.viewportWidth < 768
    },
    itemLimit () {
      return this.isMobileViewport ? ITEM_LIMIT_MOBILE : ITEM_LIMIT
    },
    shouldShowIcon () {
      return this.inputHasFocus || this.inputHasValue || this.isMenuSearchBar
    },
    visibleProducts () {
      return (this.products || []).slice(0, this.itemLimit)
    },
    searchableCategories () {
      return this.getCategories
        .filter(category => category.level > 3)
        .map(category => ({ category, normalized: normalizeTerm(category.name) }))
    },
    boundedSearch () {
      return boundSearchText(this.search)
    },
    categories () {
      if (!this.boundedSearch) {
        return []
      }

      const term = normalizeTerm(this.boundedSearch)

      return this.searchableCategories
        .filter(({ normalized }) => matchesNormalized(normalized, term))
        .sort((a, b) => {
          const aContains = a.normalized.includes(term)
          const bContains = b.normalized.includes(term)
          if (aContains !== bContains) return aContains ? -1 : 1
          return a.category.level - b.category.level
        })
        .slice(0, this.itemLimit)
        .map(({ category }) => category)
    },
    isSearching () {
      return this.inputDirty || this.pendingSearches > 0
    },
    hasNoResults () {
      return Boolean(this.boundedSearch) && !this.isSearching && this.emptyResults && !this.categories.length
    },
    didYouMean () {
      const bestMatch = this.categories[0] || this.visibleProducts[0]

      return bestMatch ? deriveDidYouMean(bestMatch.name, this.boundedSearch) : ''
    },

    getNoResultsMessage () {
      let msg = ''
      if (!this.$v.search.minLength) {
        msg = 'Searched term should consist of at least 3 characters.'
      } else if (this.emptyResults) {
        msg = 'No results were found.'
      }
      return msg
    },
    ...mapState({
      searchSuggestions: state => state.ui.searchSuggestions,
      currentRoute: state => state.url.currentRoute.name
    })
  },
  watch: {
    categories () {
      this.selectedCategoryIds = []
    },
    currentRoute () {
      this.hideSuggestions()
    },
    search (newValue) {
      this.inputHasValue = newValue.length > 0
    },
    searchSuggestions () {
      this.updateBodyScrollLock()
    }
  },
  created () {
    this.debouncedSearch = debounce(this.runSearch, SEARCH_DEBOUNCE_MS)
  },
  mounted () {
    this.hideSuggestions()
    this.inputHasValue = this.search.length > 0
    this.onResize()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize, { passive: true })
    }
    this.updateBodyScrollLock()
  },
  beforeDestroy () {
    this.debouncedSearch.cancel()
  },
  destroyed () {
    this.hideSuggestions()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
    }
    this.updateBodyScrollLock(true)
  },
  methods: {
    onSuggestionNavigate (to, eventData) {
      this.hideSuggestions(eventData)

      if (!to || !this.$router) return
      this.$router.push(to).catch(() => {})
    },
    blurSearchInput () {
      const baseInput = this.$refs?.search
      const inputEl = baseInput?.$el?.querySelector?.('input')
      if (inputEl && typeof inputEl.blur === 'function') {
        inputEl.blur()
      }
    },
    onResultsTouchStart (event) {
      if (!this.isMobileViewport) return
      this.blurSearchInput()
    },
    onResultsScroll () {
      if (!this.isMobileViewport || !this.inputHasFocus) return
      this.blurSearchInput()
    },
    onResize () {
      if (typeof window === 'undefined') return
      this.viewportWidth = window.innerWidth || 1024
    },
    closeMobileSearch () {
      this.inputHasFocus = false
      this.$store.commit('ui/setSearchSuggestions', false)
      this.clearSearch()
    },
    updateBodyScrollLock (forceUnlock = false) {
      if (typeof document === 'undefined') return
      const body = document.body
      if (!body) return
      const shouldLock = !forceUnlock && this.isMobileViewport && this.searchSuggestions

      if (shouldLock) {
        this.disableBodyScroll()
      } else {
        this.clearAllBodyScrollLocks()
      }
    },
    categoryLink (category) {
      return formatCategoryLink(category)
    },
    productLink (product) {
      return formatProductLink(product, currentStoreView().storeCode)
    },
    clearSearch () {
      this.search = ''
      this.inputHasValue = false
      this.discardPendingResults()
    },
    discardPendingResults () {
      this.searchRequestId++
      this.products = []
      this.emptyResults = false
    },
    getArtistImage (category) {
      const img = category?.image
      if (!img || img === 'no_selection') return headerPlaceholder

      return img
    },
    getCategoryEventCount (category) {
      return category?.product_count || 0
    },
    getEventDay (product) {
      if (!product?.date) return ''

      return timeToDayJS(product.date).format('DD')
    },
    getEventMonth (product) {
      if (!product?.date) return ''

      return timeToDayJS(product.date).format('MMM').toUpperCase()
    },
    getEventTime (product) {
      if (!product?.date) return ''

      return timeToDayJS(product.date).format('HH:mm')
    },
    getEventWeekdayShort (product) {
      if (!product?.date) return ''
      const weekday = timeToDayJS(product.date).format('ddd')

      return weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : ''
    },
    getLocationLabel (product) {
      const attrs = this.getAttributesByCode
      if (!attrs?.location?.options) return ''
      const opt = attrs.location.options.find(option => Number(option.value) === Number(product?.location))

      return opt ? opt.label : ''
    },
    getCityLabel (product) {
      const attrs = this.getAttributesByCode
      if (!attrs?.city?.options) return ''
      const opt = attrs.city.options.find(option => Number(option.value) === Number(product?.city))

      return opt ? opt.label : ''
    },
    hideSuggestions (event) {
      if (event) {
        this.$store.dispatch('google-tag-manager/trackEvent', {
          event: 'search',
          ecommerce: {
            search: event
          }
        })
      }
      this.search = ''
      this.inputHasValue = false
      this.discardPendingResults()
      this.$store.commit('ui/setSearchSuggestions', false)
    },
    onInputFocus () {
      this.inputHasFocus = true
    },
    onInputBlur () {
      this.inputHasFocus = false
      this.$v.search.$touch()
    },
    onInput (value) {
      this.inputHasValue = value.length > 0
      this.inputDirty = true
      this.debouncedSearch()
    },
    async makeSearch () {
      if (!this.boundedSearch) {
        this.discardPendingResults()
        return
      }

      const requestId = ++this.searchRequestId

      try {
        const { items } = await this.$store.dispatch('product/findProducts', {
          query: this.buildSearchQuery(this.search),
          start: 0,
          size: this.size,
          options: { populateRequestCacheTags: false, prefetchGroupProducts: false }
        })

        if (requestId !== this.searchRequestId) {
          return
        }

        this.products = items
        this.emptyResults = items.length < 1
      } catch (err) {
        Logger.error(err, 'search-block')()
      }
    },
    async runSearch () {
      this.debouncedSearch.cancel()
      this.inputDirty = false
      this.pendingSearches++

      try {
        await this.makeSearch()
      } finally {
        this.pendingSearches--
      }
    },
    getCategoryLabel (category) {
      if (category.is_artist) {
        return 'Artist tickets'
      } else if (category.entity_type) {
        switch (category.entity_type) {
          case 'city':
            return 'City tickets'
          case 'venue':
            return 'Venue tickets'
          case 'genre':
            return 'Genre tickets'
          default:
            return 'Category tickets'
        }
      }
      return 'Category tickets'
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/animations/transitions";
@import "~theme/css/variables/grid";
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

.search-button {
  border-radius: 0;
  background-color: transparent;

  @include desktop {
    height: 84px;
    font-size: 30px;
  }

  &-suggestion {
    border-radius: 0 23px 23px 0;
    width: 160px;
    height: 59px;
    font-size: 24px;
    padding: 0;
  }
}

.search-clear {
  appearance: none;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: 6px;
  cursor: pointer;
  flex: 0 0 auto;

  .icon {
    width: 12px;
    height: 12px;
  }
}

.menu-search {
  max-width: 309px;

  &-active {
    right: 3px;
    width: 558px;
  }
}

.suggestion-block {
  top: 52px;
  border-radius: 10px;
  width: 100%;
  z-index: 2000;

  &-icon {
    height: 15px;
    width: 15px;
    background-size: contain;
  }

  @include tablet {
    .pl11-desktop {
      padding-left: 28px;
    }

    .pl50-desktop {
      padding-left: 52px;
    }
  }

  @media (min-width: 1025px) and (max-width: 1400px) {
    margin: 0;
  }

  @media (min-width: 1401px) {
    margin: 0 50px;
  }

  @include desktop {
    top: 53px;
    width: auto;

    &-icon {
      height: 23px;
      width: 23px;
    }

    &-text {
      font-size: 24px;
      line-height: 29px;
    }
  }

  &-menu {
    top: 52px;
    right: 0;
    left: 0;
    border-radius: 23px;
    margin: 0;
  }
}

.suggestion-block-text {
  color: #fff;
}

a.suggestion-block-text:hover {
  color: #fff;
}

.search-block-input {
  margin: 0;
  position: relative;

  /deep/ .form-input {
    color: #000;
    padding: 12px 24px;
    height: 44px;
    font-size: 18px;
  }

  /deep/ .form-input::placeholder {
    color: #949492;
    font-size: 18px;
    line-height: 22px;
  }

  /deep/ .form-input:focus {
    border-color: #000;
  }

  /deep/ i {
    display: none;
  }

  &.show-search-icon {
    /deep/ .form-input {
      padding-left: 60px;
    }

    &::before {
      content: "";
      position: absolute;
      left: 24px;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      background-image: url('~theme/assets/icons/search.svg');
    }
  }
}

.search-block {
  border-radius: 24px;
  overflow: hidden;
  height: 44px;
}

.search-block--open {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.search-block--glass {
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.search-block--glass .search-block-input {
  /deep/ .form-input {
    background-color: transparent;
    color: #fff;
    border-color: transparent;
  }

  /deep/ .form-input::placeholder {
    color: #ddd;
  }
}

@media (min-width: 1025px) and (max-width: 1400px) {
  .container.p0.px50-desktop {
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;
  }

  .search-wrapper {
    margin: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

@include mobile {
  .search-block-spacer {
    height: 44px;
    visibility: hidden;
    pointer-events: none;
  }

  .search-overlay-enter-active,
  .search-overlay-leave-active {
    transition: opacity 300ms ease, transform 300ms ease;
    will-change: opacity, transform;
  }

  .search-overlay-enter,
  .search-overlay-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.985);
  }

  .suggestion-block:not(.suggestion-block-menu) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    margin: 0;
    padding: 74px 12px 16px 12px;
    border-radius: 0;
    border: 0;
    box-shadow: none;
    z-index: 10;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(20px);
  }

  .search-block--open {
    position: fixed;
    top: 12px;
    left: 12px;
    right: 12px;
    z-index: 11;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
  }

  .suggestion-mobile {
    background: transparent;
    border-radius: 0;
    padding: 0;
    max-height: calc(100vh - 90px);
    overflow: auto;
  }

  .suggestion-mobile-artist,
  .suggestion-mobile-event {
    display: flex;
    gap: 12px;
    padding: 10px 8px;
    border-radius: 12px;
    text-decoration: none;
    color: #fff;
  }

  .suggestion-mobile-artist:active,
  .suggestion-mobile-event:active {
    background: rgba(255, 255, 255, 0.1);
  }

  .suggestion-mobile-artist-thumb {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    object-fit: cover;
    flex: 0 0 auto;
    background: rgba(0, 0, 0, 0.08);
    pointer-events: none;
    -webkit-user-drag: none;
  }

  .suggestion-mobile-artist-meta,
  .suggestion-mobile-event-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .suggestion-mobile-title {
    font-size: 18px;
    line-height: 20px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-mobile-sub {
    margin-top: 2px;
    font-size: 16px;
    line-height: 22px;
    color: rgba(255, 255, 255, 0.65);
  }

  .suggestion-mobile-date {
    width: 52px;
    height: 64px;
    border-radius: 12px;
    background: color(primary);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .suggestion-mobile-date-day {
    font-size: 20px;
    line-height: 20px;
    font-weight: 700;
    color: #fff;
  }

  .suggestion-mobile-date-month {
    margin-top: 3px;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: 0;
    color: #fff;
  }

  .suggestion-mobile-event-lines {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: rgba(255, 255, 255, 0.65);
    font-size: 16px;
    line-height: 22px;
    white-space: nowrap;
    overflow: hidden;
  }

  .suggestion-mobile-line {
    display: inline-flex;
    align-items: center;
    min-width: 0;
  }

  .suggestion-mobile-line .icon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
    flex: 0 0 auto;
  }

  .suggestion-mobile-line-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.suggestion-empty {
  padding: 20px 8px;
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;

  @include mobile {
    padding: 10px 8px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.75);
  }
}

.suggestion-did-you-mean {
  padding: 12px 8px 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  line-height: 18px;
}

.search-backdrop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1999;
}

@media (min-width: 768px) {
  .col-header {
    padding-left: 8px;
  }

  .suggestion-block:not(.suggestion-block-menu) {
    top: 44px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: 0;
  }

  .suggestion-block {
    padding: 0;
    border-radius: 16px;
    overflow: hidden;
  }

  .suggestion-desktop {
    display: flex;
    width: 100%;
    min-height: 280px;
  }

  .suggestion-col {
    padding: 20px 10px;
  }

  .suggestion-col--artists {
    width: 50%;
    border-right: 1px solid rgba(255, 255, 255, 0.14);
  }

  .suggestion-col--events {
    width: 50%;
  }

  .suggestion-artist-item,
  .suggestion-event-item {
    display: flex;
    align-items: center;
    padding: 8px;
    gap: 12px;
    border-radius: 10px;
    color: #fff;
    text-decoration: none;
    cursor: pointer;
  }

  .suggestion-artist-item:hover,
  .suggestion-event-item:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .suggestion-artist-thumb {
    width: 64px;
    height: 64px;
    border-radius: 10px;
    object-fit: cover;
    flex: 0 0 auto;
    background: rgba(255, 255, 255, 0.12);
    pointer-events: none;
    -webkit-user-drag: none;
  }

  .suggestion-artist-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .suggestion-artist-count {
    color: rgba(255, 255, 255, 0.7);
    margin-top: 2px;
  }

  .suggestion-date-badge {
    width: 60px;
    height: 67px;
    border-radius: 10px;
    background: color(primary);
    border: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: flex-start;
    justify-content: center;
    flex: 0 0 auto;
  }

  .suggestion-event-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .suggestion-title {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-event-sub {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-event-line {
    display: inline-flex;
    align-items: center;
    min-width: 0;
  }

  .suggestion-event-line-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-event-sub .icon {
    opacity: 0.9;
    width: 16px;
    height: 16px;
    margin-right: 6px;
    flex: 0 0 auto;
  }
}
</style>
